import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { BehaviorSubject, Observable, Subject, takeUntil, tap } from 'rxjs';
import { BottomSheetService } from '@app/bottom-sheet.service';
import { CommonModule } from '@angular/common';
import { MonthPipe } from '@app/when/month.pipe';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { DayComponent } from '@app/when/day/day.component';
import { MobileDatePipe } from './mobile-date.pipe';
import { AvailableDate } from '@app/services/api/api.service';
import { DayToDatePipe } from '@app/when/day-to-date.pipe';
import { DirectivesModule } from '@app/directives/directives.module';
import { DateHelper } from '@app/date-helper';

@Component({
  selector: 'app-when-mobile',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MonthPipe,
    AngularSvgIconModule,
    DayComponent,
    MobileDatePipe,
    DayToDatePipe,
    DirectivesModule,
  ],
  templateUrl: './when-mobile.component.html',
  styleUrls: ['./when-mobile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: WhenMobileComponent,
    },
  ],
  animations: [
    trigger('toggleInOut', [
      state(
        'in',
        style({
          transform: 'translateX(0)',
        })
      ),
      state(
        'out',
        style({
          transform: 'translateX(100%)',
        })
      ),
      transition('in => out', [animate('0.15s ease-out')]),
      transition('out => in', [animate('0.15s ease-out')]),
    ]),
  ],
})
export class WhenMobileComponent implements ControlValueAccessor {
  @Input() public value: any;
  @Input() public activeInput: string | undefined;
  @Output() public activeInputChange = new EventEmitter<string | undefined>();

  @ViewChild('when') public when!: ElementRef;
  @Input()
  public unavailableDates: AvailableDate[];
  @Input()
  public noAvailability = false;
  public places?: Observable<any>;
  public searching = new BehaviorSubject(false);
  public focused = new BehaviorSubject(false);

  public bottomSheetOpen = new BehaviorSubject(false);
  // todo control valuea accessor on each element
  @ViewChild('whenInput')
  public whenInput?: ElementRef;

  @ViewChild('checkInOut', { static: false })
  public set checkInOut(el: ElementRef | undefined) {
    if (el) {
      const native = el?.nativeElement;

      this.observer = new IntersectionObserver(
        ([e]) => {
          return e.target.classList.toggle('stuck', e.intersectionRatio < 1);
        },
        { threshold: [1] }
      );

      this.observer.observe(native);
    } else {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = undefined;
      }
    }
  }

  private observer: IntersectionObserver | undefined;

  selection: any;
  days = Array.from({ length: 40 }, (_, i) => i + 1);
  selecting: boolean = false;

  anchorStart?: number;
  anchorEnd?: number;
  focus?: number;

  fromMonth!: number;
  fromYear!: number;

  toMonth!: number;
  toYear!: number;

  toDays!: number;
  fromDays!: number;

  toStartDay!: number;
  fromStartDay!: number;

  private touched = false;

  public edges = [];
  public corners = [];

  public startDayOfMonth: number | undefined;
  public endDayOfMonth: number | undefined;

  public absoluteAnchorStart: number | undefined = 0;
  public absoluteAnchorEnd: number | undefined = 0;
  public monthOffset = 0;

  public checkInOutState = 'in';

  private destroyed = new Subject<void>();

  public startDate: Date | undefined;
  public endDate: Date | undefined;

  public anchorStartDate: any;
  public anchorEndDate: any;

  constructor(private bs: BottomSheetService) {}

  onChange = (value: string[]) => {};
  onTouched = () => {};

  ngOnInit() {
    // const daysInMonth = getDaysInMonth(new Date(2023, 11, 1).getMonth());
    this.fromMonth = new Date().getMonth();
    this.fromYear = new Date().getFullYear();
    this.toYear = this.fromMonth === 11 ? this.fromYear + 1 : this.fromYear;
    this.toMonth = this.fromMonth === 11 ? 0 : this.fromMonth + 1;

    this.fromDays = DateHelper.getDaysInMonth(this.fromMonth, this.fromYear);
    this.toDays = DateHelper.getDaysInMonth(this.toMonth, this.toYear);

    this.fromStartDay = DateHelper.getFirstDayOfMonth(
      this.fromMonth,
      this.fromYear
    );
    this.toStartDay = DateHelper.getFirstDayOfMonth(this.toMonth, this.toYear);

    this.focused
      .pipe(
        tap((focused) => {
          console.log(
            'Focused changed:',
            focused,
            'activeInput:',
            this.activeInput
          );
          if (focused && this.activeInput === 'when') {
            this.openBottomSheet();
          } else {
            this.closeBottomSheet();
          }
          if (focused && this.value) {
            // do something if date already was selected
          } else if (focused && !this.value) {
            // do something if date already was selected
          }
        }),
        takeUntil(this.destroyed)
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['activeInput']?.previousValue !== 'when' &&
      changes['activeInput']?.currentValue === 'when'
    ) {
      this.setFocus(null, true);
    }

    // if (changes['active']?.currentValue) {
    //   this.setFocus(null, true);
    // }
  }

  public openBottomSheet() {
    this.bottomSheetOpen.next(true);
  }

  public closeBottomSheet() {
    this.bottomSheetOpen.next(false);
  }

  public getDaysInMonth(month: number, year: number) {
    return new Date(year, month, 0).getDate();
  }

  public getFirstDayOfMonth(month: number, year: number) {
    var firstDay = new Date(year, month - 1, 1);
    return firstDay.getDay();
  }

  // is this working correctly?
  public highlight(i: number) {
    if (this.anchorStart !== undefined) {
      if (i > this.anchorStart) {
        this.focus = i;
      }
    }
  }

  public unhighlight() {
    this.focus = undefined;
  }

  setFocus(event: any, focused: boolean) {
    // Removed call to doUpdate() to prevent recursion
    // Force emission if already at the same value
    if (this.focused.value === focused) {
      this.focused.next(!focused);
    }
    this.focused.next(focused);
  }

  public clicked(day: number, dayOfMonth: number) {
    this.selecting = true;
    if (this.checkInOutState === 'in') {
      if (
        day === this.anchorStart &&
        this.monthOffset === this.absoluteAnchorStart
      ) {
        this.anchorStart = undefined;
        this.startDate = undefined;
        this.absoluteAnchorStart = undefined;
        this.absoluteAnchorEnd = undefined;
        this.startDayOfMonth = undefined;
        this.endDayOfMonth = undefined;
      } else if (
        this.anchorEnd &&
        ((this.anchorEnd < day &&
          this.monthOffset === this.absoluteAnchorEnd) ||
          (this.absoluteAnchorEnd && this.absoluteAnchorEnd < this.monthOffset))
      ) {
        // click on a future month
        this.absoluteAnchorStart = this.monthOffset;
        this.anchorStart = day;
        this.startDate = this.getDate(day);
        this.startDayOfMonth = dayOfMonth;
        this.checkInOutState = 'out';

        this.anchorEnd = undefined;
        this.endDate = undefined;
        this.absoluteAnchorEnd = undefined;
        this.endDayOfMonth = undefined;
      } else {
        this.absoluteAnchorStart = this.monthOffset;
        this.anchorStart = day;
        this.startDate = this.getDate(day);
        this.startDayOfMonth = dayOfMonth;
        this.checkInOutState = 'out';
      }
    } else {
      // if a day less tha nthe current anchor start is selected, set that as the anchor start
      // even if 'check out' is selected
      if (
        this.absoluteAnchorStart &&
        this.absoluteAnchorStart < this.monthOffset
      ) {
        // click on a future month
        this.absoluteAnchorEnd = this.monthOffset;
        this.anchorEnd = day;
        this.endDate = this.getDate(day);
        // this is causing previous day to be highlighted temporarily on click
        this.anchorEndDate = DateHelper.normalizeDateToDateString(this.endDate);
        this.endDayOfMonth = dayOfMonth;
        this.done(day);
      } else if (
        this.absoluteAnchorStart &&
        this.absoluteAnchorStart > this.monthOffset
      ) {
        // click on a previous month
        this.anchorStart = day;
        this.startDate = this.getDate(day);
        // this is causing previous day to be highlighted temporarily on click
        this.anchorStartDate = DateHelper.normalizeDateToDateString(
          this.startDate
        );
        this.absoluteAnchorStart = this.monthOffset;
        this.absoluteAnchorEnd = undefined;
        this.anchorEnd = undefined;
        this.endDate = undefined;
        this.anchorStartDate = undefined;
        this.startDayOfMonth = dayOfMonth;
        this.endDayOfMonth = undefined;
      } else if (day === this.anchorStart) {
        this.anchorStart = undefined;
        this.anchorStartDate = undefined;
        this.startDate = undefined;
        this.absoluteAnchorStart = undefined;
        this.absoluteAnchorEnd = undefined;
        this.startDayOfMonth = undefined;
        this.endDayOfMonth = undefined;
      } else if (
        this.anchorStart !== undefined &&
        this.anchorEnd !== undefined
      ) {
        this.anchorStart = day;
        this.startDate = this.getDate(day);
        // this is causing previous day to be highlighted temporarily on click
        this.anchorStartDate = DateHelper.normalizeDateToDateString(
          this.startDate
        );
        this.absoluteAnchorStart = this.monthOffset;
        this.absoluteAnchorEnd = undefined;
        this.anchorEnd = undefined;
        this.anchorEndDate = undefined;
        this.endDate = undefined;
        this.startDayOfMonth = dayOfMonth;
        this.endDayOfMonth = undefined;
      } else if (this.anchorStart !== undefined && day > this.anchorStart) {
        this.absoluteAnchorEnd = this.monthOffset;
        this.anchorEnd = day;
        this.endDate = this.getDate(day);
        // this is causing previous day to be highlighted temporarily on click
        this.anchorEndDate = DateHelper.normalizeDateToDateString(this.endDate);
        this.endDayOfMonth = dayOfMonth;
        this.done(day);
      } else {
        this.anchorStart = day;
        this.startDate = this.getDate(day);
        // this is causing previous day to be highlighted temporarily on click
        this.anchorStartDate = DateHelper.normalizeDateToDateString(
          this.startDate
        );
        this.absoluteAnchorStart = this.monthOffset;
        this.anchorEnd = undefined;
        this.anchorEndDate = undefined;
        this.endDate = undefined;
        this.absoluteAnchorEnd = undefined;
        this.startDayOfMonth = dayOfMonth;
        this.endDayOfMonth = undefined;
      }
    }
  }

  private getDate(selected: number): Date {
    let year;
    let month;
    let day;

    if (selected > 40) {
      year = this.toYear;
      month = this.toMonth;
      day = selected - 40 - this.toStartDay;
    } else {
      year = this.fromYear;
      month = this.fromMonth;
      day = selected - this.fromStartDay;
    }

    return new Date(year, month, day);
  }

  public done(day: number) {
    // this.doUpdate();
  }

  public onBlurHandler() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  public writeValue(value: any): void {
    this.value = value;
  }

  public registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  public doUpdate() {
    this.value = [
      DateHelper.formatDateToLocalString(this.startDate),
      DateHelper.formatDateToLocalString(this.endDate),
    ];
    this.onChange(this.value);
    this.setFocus(null, false);
    this.closeBottomSheet();

    // Delay emitting activeInputChange to allow bottom sheet to close
    setTimeout(() => {
      this.activeInputChange.emit('who');
    }, 0);
  }

  skip() {
    this.focused.next(false);
    this.activeInputChange.emit('who');
  }

  reset() {
    this.anchorStart = undefined;
    this.anchorEnd = undefined;
    this.absoluteAnchorEnd = undefined;
    this.absoluteAnchorStart = undefined;
    this.value = undefined;
    this.startDayOfMonth = undefined;
    this.endDayOfMonth = undefined;
    this.onChange(this.value);
  }

  public nextMonth() {
    if (this.anchorStart !== undefined) {
      this.anchorStart = this.anchorStart - 40;
    }
    if (this.anchorEnd !== undefined) {
      this.anchorEnd = this.anchorEnd - 40;
    }
    this.monthOffset = this.monthOffset + 1;
    this.fromMonth = this.toMonth;
    this.fromYear = this.toYear;
    this.toYear = this.toMonth === 11 ? this.toYear + 1 : this.toYear;
    this.toMonth = this.fromMonth === 11 ? 0 : this.toMonth + 1;

    this.fromDays = DateHelper.getDaysInMonth(this.fromMonth, this.fromYear);
    this.toDays = DateHelper.getDaysInMonth(this.toMonth, this.toYear);

    this.fromStartDay = DateHelper.getFirstDayOfMonth(
      this.fromMonth,
      this.fromYear
    );
    this.toStartDay = DateHelper.getFirstDayOfMonth(this.toMonth, this.toYear);
  }

  // shift anchors by 40! should I do this? seems like it messes things up
  public previousMonth() {
    if (this.anchorStart !== undefined) {
      this.anchorStart = this.anchorStart + 40;
    }
    if (this.anchorEnd !== undefined) {
      this.anchorEnd = this.anchorEnd + 40;
    }
    this.monthOffset = this.monthOffset - 1;
    this.toMonth = this.fromMonth;
    this.toYear = this.fromYear;
    this.fromYear = this.fromMonth === 0 ? this.fromYear - 1 : this.fromYear;
    if (this.fromMonth === 0) {
      this.fromMonth = 11;
    } else {
      this.fromMonth = this.fromMonth - 1;
    }

    this.fromDays = DateHelper.getDaysInMonth(this.fromMonth, this.fromYear);
    this.toDays = DateHelper.getDaysInMonth(this.toMonth, this.toYear);

    this.fromStartDay = DateHelper.getFirstDayOfMonth(
      this.fromMonth,
      this.fromYear
    );
    this.toStartDay = DateHelper.getFirstDayOfMonth(this.toMonth, this.toYear);
  }

  public toggleCheckInOut(state: string) {
    this.checkInOutState = state;
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
    this.closeBottomSheet();
  }
}
